import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError, timeout } from 'rxjs';
import { catchError, concatMap, finalize, tap } from 'rxjs/operators';
import { LoginService } from './login.service';
import { ILoginDto } from './models/login-dto';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { INgxsFormModel, IUser, UserService, WwwAppStateSetUserData } from '@pro-connect/shared';

export class LoginStateLogin {
  public static readonly type = '[LoginState] login';
}

export interface LoginStateModel {
  loading: boolean;
  form: INgxsFormModel<{
    username: string;
    password: string;
  }>;
  errorMessage: string | null;
}

@State<LoginStateModel>({
  name: 'login',
  defaults: {
    loading: false,
    form: {
      model: {
        username: '',
        password: '',
      },
      errors: {},
      dirty: false,
      disabled: false,
      status: 'pristine',
    },
    errorMessage: null,
  },
})
@Injectable()
export class LoginState {
  public constructor(
    private readonly _loginService: LoginService,
    private readonly _userService: UserService,
    private readonly _recaptchaV3Service: ReCaptchaV3Service,
  ) {}

  @Selector()
  public static loading({ loading }: LoginStateModel): boolean {
    return loading;
  }

  @Selector()
  public static errorMessage({ errorMessage }: LoginStateModel): string | null {
    return errorMessage;
  }

  @Action(LoginStateLogin)
  public login({ dispatch, getState, patchState }: StateContext<LoginStateModel>) {
    const {
      form: { model },
    } = getState();
    patchState({ loading: true, errorMessage: null });
    const loginDto: ILoginDto = { ...model };

    return this._recaptchaV3Service.execute('importantAction').pipe(
      timeout({ first: 5000, with: () => throwError(() => 'timeout') }),
      concatMap((recaptchaKey: string) => this._loginService.login(loginDto, recaptchaKey)),
      catchError((error) => {
        if (error?.error?.code === 401) {
          patchState({ errorMessage: error.error.message });
        }
        return throwError(() => error);
      }),
      concatMap(() => this._userService.getMe()),
      tap((user: IUser) => {
        dispatch([new WwwAppStateSetUserData({ user })]);
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }
}
